import Avatar from './Avatar';
import GroupChip from './GroupChip';
import PetBadge from './PetBadge';
import SurpriseBadge from './SurpriseBadge';
import { formatDateOnly } from '../utils/dates';
import { wishOwner } from '../utils/wishOwner';

// The Shop feed's wish card. This is NOT the shared WishGiftCard — it has a
// distinct avatar-led horizontal layout, group chips, and a `status`
// state-machine prop — so it carries its own owner-blindness guard rather than
// reusing that component. Lives in its own file so it can be unit-tested without
// pulling the whole Feed page (and its router-bound AppLayout) into the test.

function priceDisplay(wish) {
    const money = (raw) => {
        if (raw == null) return null;
        const n = Number(raw);
        if (Number.isNaN(n)) return null;
        return n === Math.round(n) ? `$${Math.round(n)}` : `$${n.toFixed(2)}`;
    };
    const lo = money(wish.price_min);
    const hi = money(wish.price_max);
    if (lo && hi) return lo === hi ? lo : `${lo} – ${hi}`;
    return lo || (hi ? `up to ${hi}` : null);
}

// Gift state from the viewer's perspective — mirrors iOS Wish.giftStatus.
export function giftStatus(wish, userId) {
    const purchases = wish.purchases || [];
    const mine = purchases.find((p) => p.purchased_by_user_id === userId);
    if (mine) return { kind: 'purchasedByMe', label: 'You bought this', color: 'text-slate-600' };
    if (wish.status === 'purchased' || wish.status === 'revealed') {
        return { kind: 'purchasedByOther', label: `Bought by ${purchases[0]?.purchased_by?.name || 'someone'}`, color: 'text-slate-500' };
    }
    if (wish.claimed_by_user_id) {
        return wish.claimed_by_user_id === userId
            ? { kind: 'claimedByMe', label: 'You claimed this', color: 'text-amber-600' }
            : { kind: 'claimedByOther', label: `Claimed by ${wish.claimed_by?.name || 'someone'}`, color: 'text-amber-600' };
    }
    return { kind: 'available', label: 'Available', color: 'text-emerald-600' };
}

// Compact one-line tally for a shareable ("chip in") wish, shown to gifters
// only. Counts non-withdrawn contributions. Returns null when not shareable.
function shareTally(wish) {
    const shareable = wish.share_count != null || wish.estimated_price != null || !!wish.allow_partial_funding;
    if (!shareable) return null;
    const active = (wish.contributions || []).filter((c) => c.status !== 'withdrawn');
    if (wish.share_count != null) {
        const pieces = active.reduce((t, c) => t + (Number(c.shares_pledged) || 0), 0);
        return `${pieces} of ${wish.share_count} shares claimed`;
    }
    const money = (raw) => {
        const n = Number(raw);
        return n === Math.round(n) ? `$${Math.round(n)}` : `$${n.toFixed(2)}`;
    };
    const pledged = active.reduce((t, c) => t + (Number(c.amount) || 0), 0);
    if (wish.estimated_price != null) return `${money(pledged)} of ${money(wish.estimated_price)} pledged`;
    return `${money(pledged)} pledged so far`;
}

export default function FeedWishCard({ wish, user, status, busy, onClaim, onUnclaim, onPurchase, onUndo, onSuggest }) {
    const price = priceDisplay(wish);
    const owner = wishOwner(wish);
    const tally = shareTally(wish);

    // OWNER-BLINDNESS (defense in depth, on top of the API's feed exclusion).
    // Keyed off `wish.user_id` (the wish itself), not off whatever the feed query
    // returned, so if the feed ever starts including the viewer's own wishes (e.g.
    // a "my wishes" filter chip) we still never render claim/purchase state or
    // gifting buttons for the owner. See CLAUDE.md "Rules".
    const isMine = wish.user_id === user?.id;

    return (
        <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-start gap-3">
                <Avatar name={owner.name} url={owner.avatarUrl} color={owner.avatarColor} seed={owner.seed} size={40} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-gray-900">{wish.title}</p>
                        {price && <span className="text-sm text-gray-500 whitespace-nowrap">{price}</span>}
                    </div>
                    <p className="text-sm text-gray-500 flex items-center gap-1.5">
                        {owner.name}
                        {owner.isPet && <PetBadge species={owner.species} />}
                    </p>

                    {wish.suggested_by_user_id && <SurpriseBadge wish={wish} className="mt-2" />}

                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        {wish.type === 'charity' && (
                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                💝 Charity{wish.organization_name ? ` · ${wish.organization_name}` : ''}
                            </span>
                        )}
                        {(wish.groups || []).map((g) => (
                            <GroupChip key={g.id} name={g.name} icon={g.icon} color={g.color} seed={g.id} />
                        ))}
                        {wish.category && <span className="text-xs text-gray-400">{wish.category.icon} {wish.category.name}</span>}
                        {onSuggest && (
                            <button
                                onClick={onSuggest}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                title="Recommend this to someone"
                            >
                                Recommend to…
                            </button>
                        )}
                    </div>

                    {wish.event && (
                        <p className="text-xs text-rose-600 font-medium mt-2">
                            🎉 {wish.event.name} · {formatDateOnly(wish.event.event_date)}
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        {isMine ? (
                            // OWNER VIEW — no status label, no gifting buttons, ever.
                            <span className="text-xs font-medium px-2 py-1 rounded bg-blue-50 text-blue-600">Your wish</span>
                        ) : (
                            <>
                                <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
                                {tally && (
                                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-teal-50 text-teal-700">
                                        🎁 {tally}
                                    </span>
                                )}
                                <span className="flex-1" />
                                <GiftActions status={status} busy={busy} multipleOk={wish.multiple_ok}
                                    onClaim={onClaim} onUnclaim={onUnclaim} onPurchase={onPurchase} onUndo={onUndo} />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function GiftActions({ status, busy, multipleOk, onClaim, onUnclaim, onPurchase, onUndo }) {
    const btn = 'px-3 py-1 rounded-md text-sm font-medium disabled:opacity-50';
    switch (status.kind) {
        case 'available':
            return (
                <>
                    <button disabled={busy} onClick={onClaim} className={`${btn} bg-gray-100 text-gray-700 hover:bg-gray-200`}>Claim</button>
                    <button disabled={busy} onClick={onPurchase} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Mark purchased</button>
                </>
            );
        case 'claimedByMe':
            return (
                <>
                    <button disabled={busy} onClick={onUnclaim} className={`${btn} bg-gray-100 text-gray-700 hover:bg-gray-200`}>Release</button>
                    <button disabled={busy} onClick={onPurchase} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Mark purchased</button>
                </>
            );
        case 'claimedByOther':
            return <button disabled={busy} onClick={onPurchase} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Mark purchased</button>;
        case 'purchasedByMe':
            return (
                <>
                    <button disabled={busy} onClick={onUndo} className={`${btn} bg-red-50 text-red-600 hover:bg-red-100`}>Undo</button>
                    {multipleOk && <button disabled={busy} onClick={onPurchase} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Buy another</button>}
                </>
            );
        case 'purchasedByOther':
            return multipleOk
                ? <button disabled={busy} onClick={onPurchase} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}>Mark purchased</button>
                : <span className="text-xs text-gray-400">Already purchased</span>;
        default:
            return null;
    }
}
